import React, { useEffect, useState } from "react";
import { Box, Button, Flex, Icon, Text, Textarea } from "@chakra-ui/react";
import { HiChevronDown, HiChevronUp, HiOutlineXMark, HiCheck } from "react-icons/hi2";
import { c, popupSurface } from "../../classroomColors";
import { STANDE } from "../../standeTokens";
import { KIND_LABEL, secondsLeft } from "./useLiveActivity";
import { Chip, Countdown, Eyebrow, MarkPill, QuestionBody, inkButton, useTick } from "./parts";
import { EscapeBoard } from "./ActivityStage";

/** Answering one question: the options, or a box to write in, and Send. */
const Answer = ({ question, onSend, closed, closedText }) => {
  const [text, setText] = useState("");
  const [picked, setPicked] = useState(null);
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState(null);
  const value = question.is_mcq ? picked : text.trim();

  const send = async () => {
    if (!value) return;
    setSending(true);
    setProblem(null);
    const error = await onSend(question, value);
    if (error) setProblem(error);
    setSending(false);
  };

  return (
    <Box>
      <QuestionBody question={question} picked={picked} onPick={closed ? undefined : setPicked} disabled={closed} />
      {!question.is_mcq && (
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Your answer" mt="12px" rows={4}
          isDisabled={closed} bg={c.surface} borderColor={c.borderSoft} borderRadius="12px" fontSize="14px" color={c.text}
          _focusVisible={{ borderColor: c.solidBg, boxShadow: "none" }} />
      )}
      <Flex align="center" justify="space-between" mt="12px" gap="10px">
        <Text fontSize="12px" color={problem ? c.accentSoftText : c.textMuted}>
          {problem || (closed ? closedText : `${question.total_mark} ${question.total_mark === 1 ? "mark" : "marks"}`)}
        </Text>
        <Button {...inkButton} h="40px" px="22px" borderRadius="12px" fontSize="14px" fontWeight="600"
          isDisabled={!value || closed} isLoading={sending} onClick={send}>
          Send answer
        </Button>
      </Flex>
    </Box>
  );
};

/** After sending: in, being marked, then — once the teacher sends it — the
 *  mark and what to do better. */
const Sent = ({ mine, question }) => (
  <Box p="14px" borderRadius="14px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
    <Flex align="center" justify="space-between" gap="10px">
      <Flex align="center" gap="8px">
        <Flex boxSize="22px" borderRadius="full" bg={STANDE.darkGreen} color={STANDE.cream} align="center" justify="center">
          <Icon as={HiCheck} boxSize="13px" />
        </Flex>
        <Text fontSize="13.5px" fontWeight="600" color={c.text}>
          {mine.released ? "Your feedback" : "Your answer is in"}
        </Text>
      </Flex>
      {mine.released && <MarkPill mark={mine.mark} total={question.total_mark} />}
    </Flex>
    <Text fontSize="12.5px" color={c.textSub} mt="6px" whiteSpace="pre-wrap">
      {mine.released ? mine.feedback : "Your teacher will send your mark and feedback."}
    </Text>
  </Box>
);

/**
 * The activity on a student's screen: over the canvas, folding to a pill
 * when they want the board back.
 *
 * A race shows the open question and its clock; a solo solve, every
 * question with where each stands; an escape room, the class's locks — any
 * shut one opens to answer, and a wrong guess can be tried again. Marks
 * and feedback appear when the teacher sends them, never before.
 */
const StudentActivity = ({ live }) => {
  const { activity, act, clock } = live;
  const [folded, setFolded] = useState(false);
  const [index, setIndex] = useState(0);
  const [lock, setLock] = useState(null);
  const [unlockError, setUnlockError] = useState(null);
  const now = useTick(clock, activity?.kind === "QUIZ_RACE" && activity?.status === "LIVE");

  // A new question, or a new activity, brings the sheet back up.
  const stamp = `${activity?.guid}:${activity?.current_index}`;
  useEffect(() => {
    setFolded(false);
    setLock(null);
  }, [stamp]);

  if (!activity || activity.status === "ARCHIVED") return null;

  const mineFor = (q) => activity.mine.find((m) => m.question_guid === q.guid);
  const send = async (question, text) => {
    let problem = null;
    await act(async (api, s, id) => {
      try {
        await api.answer(s, id, question.guid, text);
      } catch (e) {
        problem = e.message;
      }
      return null;
    });
    return problem;
  };

  const isLive = activity.status === "LIVE";
  const title = KIND_LABEL[activity.kind];

  if (folded) {
    return (
      <Flex position="absolute" bottom="20px" left="50%" transform="translateX(-50%)" zIndex={24} as="button" type="button"
        onClick={() => setFolded(false)} align="center" gap="10px" pl="16px" pr="12px" h="44px" borderRadius="200px"
        {...popupSurface} data-no-pan>
        <Box boxSize="8px" borderRadius="full" bg={isLive ? STANDE.darkGreen : c.textMuted} />
        <Text fontSize="13.5px" fontWeight="600" color={c.text}>{title}</Text>
        <Text fontSize="12.5px" color={c.textSub}>{isLive ? "Open to answer" : "See your feedback"}</Text>
        <Icon as={HiChevronUp} boxSize="15px" color={c.textSub} />
      </Flex>
    );
  }

  let body;
  if (activity.kind === "QUIZ_RACE") {
    const q = activity.questions[activity.current_index];
    const mine = mineFor(q);
    const left = secondsLeft(activity, now);
    const timeUp = left === 0;
    body = (
      <Box display="grid" gridTemplateColumns={{ base: "1fr", md: activity.leaderboard.length ? "1.6fr 1fr" : "1fr" }} gap="18px">
        <Box>{mine ? <><QuestionBody question={q} /><Box mt="12px"><Sent mine={mine} question={q} /></Box></> : (
          <Answer question={q} onSend={send} closed={!isLive || timeUp} closedText={timeUp ? "Time's up on this one" : "This question has closed"} />
        )}</Box>
        {activity.leaderboard.length > 0 && (
          <Box>
            <Eyebrow mb="8px">Leaderboard</Eyebrow>
            <Flex direction="column" gap="5px">
              {activity.leaderboard.slice(0, 5).map((r, i) => (
                <Flex key={r.user_guid} align="center" gap="8px" p="7px 10px" borderRadius="10px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
                  <Text w="14px" fontSize="12px" fontWeight="700" color={c.textMuted}>{i + 1}</Text>
                  <Text flex="1" fontSize="13px" color={c.text} noOfLines={1}>{r.name}</Text>
                  <Text fontSize="13px" fontWeight="700" color={c.text}>{r.points}</Text>
                </Flex>
              ))}
            </Flex>
          </Box>
        )}
      </Box>
    );
  } else if (activity.kind === "ESCAPE_ROOM") {
    if (lock) {
      const mine = mineFor(lock);
      const lockIndex = activity.questions.findIndex((q) => q.guid === lock.guid);
      const lockState = activity.locks[lockIndex];
      const tried = mine && mine.marking_status !== "MARKING" && !lockState?.cracked;
      body = (
        <Box>
          <Flex align="center" justify="space-between" mb="12px">
            <Chip tone="blue">{lockState?.name}</Chip>
            <Button size="sm" variant="ghost" color={c.textSub} onClick={() => setLock(null)}>Back to the locks</Button>
          </Flex>
          {lockState?.cracked ? (
            // The moment the lock gives: the digit, and back to the room.
            <Flex direction="column" align="center" textAlign="center" gap="8px" py="18px">
              <Flex boxSize="64px" borderRadius="18px" bg={STANDE.black} color={STANDE.cream} align="center" justify="center"
                fontSize="32px" fontWeight="700">
                {lockState.digit}
              </Flex>
              <Text fontSize="18px" fontWeight="600" color={c.text}>{lockState.name} is open</Text>
              <Text fontSize="13px" color={c.textSub}>Cracked by {lockState.cracked_by}. That digit is part of the door's code.</Text>
              <Button {...inkButton} mt="6px" h="40px" borderRadius="12px" onClick={() => setLock(null)}>Back to the locks</Button>
            </Flex>
          ) : mine && mine.marking_status === "MARKING" ? (
            <><QuestionBody question={lock} /><Text fontSize="13px" color={c.textSub} mt="12px">Checking your answer…</Text></>
          ) : (
            <>
              {tried && <Text fontSize="13px" color={c.accentSoftText} mb="10px">Not quite — the lock held. Try again.</Text>}
              <Answer key={mine?.answer_text || "first"} question={lock} onSend={send} closed={!isLive} closedText="The room has closed" />
            </>
          )}
        </Box>
      );
    } else {
      body = (
        <EscapeBoard
          activity={activity}
          onOpenLock={isLive ? setLock : undefined}
          unlockError={unlockError}
          onUnlock={isLive ? async (code) => {
            setUnlockError(null);
            const ok = await act((api, s, id) => api.unlock(s, id, code));
            if (!ok) setUnlockError("That code does not open the door.");
          } : undefined}
        />
      );
    }
  } else {
    const i = Math.min(index, activity.questions.length - 1);
    const q = activity.questions[i];
    const mine = mineFor(q);
    body = (
      <Box>
        <Flex gap="6px" mb="14px" wrap="wrap">
          {activity.questions.map((qq, n) => {
            const m = mineFor(qq);
            const on = n === i;
            return (
              <Flex key={qq.guid} as="button" type="button" onClick={() => setIndex(n)} align="center" gap="6px" px="12px" h="30px"
                borderRadius="200px" fontSize="12px" fontWeight={on ? "600" : "400"} bg={on ? c.solidBg : c.surface}
                color={on ? c.solidText : c.text} border="1px solid" borderColor={on ? c.solidBg : c.borderSoft}>
                {m && <Box boxSize="6px" borderRadius="full" bg={m.released ? STANDE.primaryBlue : STANDE.darkGreen} />}
                Question {n + 1}
              </Flex>
            );
          })}
        </Flex>
        {mine ? <><QuestionBody question={q} /><Box mt="12px"><Sent mine={mine} question={q} /></Box></> : (
          <Answer key={q.guid} question={q} onSend={send} closed={!isLive} closedText="This activity has ended" />
        )}
      </Box>
    );
  }

  return (
    <Flex position="absolute" inset="0" zIndex={24} align="center" justify="center" p={{ base: "12px", md: "40px" }}
      pointerEvents="none" data-no-pan>
      <Box pointerEvents="auto" w="min(860px, 100%)" maxH="100%" overflowY="auto" p={{ base: "16px", md: "24px" }}
        borderRadius="24px" {...popupSurface} sx={{ animation: "standeDockIn 0.2s ease-out" }} data-own-scroll>
        <Flex align="center" gap="8px" mb="16px" wrap="wrap">
          <Chip tone="ink" h="26px" fontSize="12px">
            {title}
            {activity.kind === "QUIZ_RACE" ? ` · Question ${activity.current_index + 1} of ${activity.questions.length}` : ""}
          </Chip>
          {!isLive && <Chip>{activity.escaped ? "Escaped" : "Ended"}</Chip>}
          <Box flex="1" />
          {activity.kind === "QUIZ_RACE" && isLive && <Countdown activity={activity} clock={clock} />}
          <Flex as="button" type="button" aria-label="Fold away" title="Fold away" onClick={() => setFolded(true)}
            boxSize="30px" borderRadius="full" align="center" justify="center" color={c.textSub} _hover={{ bg: c.surface, color: c.text }}>
            <Icon as={isLive ? HiChevronDown : HiOutlineXMark} boxSize="16px" />
          </Flex>
        </Flex>
        {body}
      </Box>
    </Flex>
  );
};

export default StudentActivity;
